import React from "react";
import {
  Alert,
  Box,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

const PRESETS = {
  product: {
    sellable: true,
    purchasable: true,
    stockTracked: true,
    reservable: false,
    shippable: true,
    publicVisible: true,
    expenseEnabled: false,
    internalConsumptionEnabled: false,
  },
  service: {
    sellable: true,
    purchasable: false,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: true,
    expenseEnabled: false,
    internalConsumptionEnabled: false,
  },
  supply: {
    sellable: false,
    purchasable: true,
    stockTracked: true,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: true,
  },
  expense: {
    sellable: false,
    purchasable: true,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: false,
  },
  asset: {
    sellable: false,
    purchasable: true,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: false,
  },
  charge: {
    sellable: true,
    purchasable: true,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: false,
  },
};

export function capabilitiesForKind(kind) {
  return { ...(PRESETS[kind] || PRESETS.product) };
}

function CapabilitySwitch({ label, detail, checked, onChange, compact = false }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.035)",
        border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.07)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
        <Box>
          <Typography sx={{ fontWeight: 850, color: BRAND.dark }}>{label}</Typography>
          {!compact ? (
            <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
              {detail}
            </Typography>
          ) : null}
        </Box>
        <Switch checked={checked} onChange={(event) => onChange(event.target.checked)} />
      </Stack>
    </Box>
  );
}

function AccountSelect({ label, value, onChange, accounts, allowedTypes }) {
  const available = (accounts || []).filter(
    (account) => account.active !== false && (!allowedTypes?.length || allowedTypes.includes(account.type)),
  );

  return (
    <TextField select label={label} value={value || ""} onChange={onChange} fullWidth>
      {available.map((account) => (
        <MenuItem key={account.code} value={account.code}>
          {account.name}
        </MenuItem>
      ))}
    </TextField>
  );
}


export default function ItemOperationalFields({
  form,
  setForm,
  canManageCosts = false,
  canManageAccounting = false,
  accounts = [],
  taxProfiles = [],
  membershipsEnabled = false,
  compact = false,
}) {
  const change = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const changeCapability = (field, value) =>
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "reservable" ? { requiresBooking: value } : {}),
      ...(field === "shippable" ? { requiresShipping: value } : {}),
    }));
  const activeTaxProfiles = (taxProfiles || []).filter((profile) => profile.active !== false);
  const selectedTax = activeTaxProfiles.find((profile) => profile.code === form.taxProfileCode);
  const itemKind = String(form.erpItemKind || form.itemType || "product").toLowerCase();
  const supportsInventory = itemKind === "product" || itemKind === "supply";
  const supportsShipping = itemKind === "product";
  const tracksInventory = supportsInventory && form.stockTracked === true;
  const hasUsefulCost = Number(form.averageCost || form.lastCost || form.standardCost || 0) > 0;

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 3 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
              {compact ? "Dónde aparece" : "Cómo participa en la operación"}
            </Typography>
            {!compact ? (
              <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                Activá solamente los usos reales. Esto define dónde aparece el ítem y qué ocurre al venderlo, comprarlo o consumirlo.
              </Typography>
            ) : null}
          </Box>
          <Divider />
          <Grid container spacing={1.3}>
            <Grid item xs={12} md={6}>
              <CapabilitySwitch
                compact={compact}
                label="Se puede vender"
                detail="Aparece en ventas e ingresos de caja."
                checked={Boolean(form.sellable)}
                onChange={(value) => change("sellable", value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CapabilitySwitch
                compact={compact}
                label="Se puede comprar"
                detail="Aparece en compras y egresos de caja."
                checked={Boolean(form.purchasable)}
                onChange={(value) => change("purchasable", value)}
              />
            </Grid>
            {supportsInventory ? (
              <Grid item xs={12} md={6}>
                <CapabilitySwitch
                  compact={compact}
                  label="Se vende sin stock"
                  detail="No controla existencias ni bloquea ventas por cantidad. Si controlás stock, toda entrada o salida se registra únicamente desde Movimientos."
                  checked={!tracksInventory}
                  onChange={(value) => change("stockTracked", !value)}
                />
              </Grid>
            ) : null}
            {supportsShipping ? (
              <Grid item xs={12} md={6}>
                <CapabilitySwitch
                  compact={compact}
                  label="Admite envío o entrega"
                  detail="El cliente puede recibir este producto mediante una entrega física."
                  checked={Boolean(form.shippable ?? form.requiresShipping)}
                  onChange={(value) => changeCapability("shippable", value)}
                />
              </Grid>
            ) : null}
            <Grid item xs={12} md={6}>
              <CapabilitySwitch
                compact={compact}
                label="Visible en la página"
                detail="Puede mostrarse en el catálogo público."
                checked={Boolean(form.publicVisible)}
                onChange={(value) => change("publicVisible", value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CapabilitySwitch
                compact={compact}
                label="Se puede registrar como gasto"
                detail="Aparece como concepto de gasto pagado."
                checked={Boolean(form.expenseEnabled)}
                onChange={(value) => change("expenseEnabled", value)}
              />
            </Grid>
            {tracksInventory ? (
              <Grid item xs={12} md={6}>
                <CapabilitySwitch
                  compact={compact}
                  label="Permite consumo interno"
                  detail="Descuenta inventario sin registrarlo como venta."
                  checked={Boolean(form.internalConsumptionEnabled)}
                  onChange={(value) => change("internalConsumptionEnabled", value)}
                />
              </Grid>
            ) : null}
          </Grid>
          {supportsInventory && !tracksInventory ? (
            <Alert severity="info">
              Este ítem se vende sin stock: la cantidad guardada no se usa como inventario real y los pedidos no la descuentan. Si volvés a activar control de stock, la existencia real debe cargarse desde Movimientos.
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      {membershipsEnabled ? (
        <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 3, borderColor: form.membershipEligible ? "rgba(243,90,58,.28)" : undefined }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Tipo de membresía</Typography>
              {!compact ? (
                <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                  Usá este mismo ítem como plan recurrente. Precio, periodicidad y cupo se copian a cada período para no alterar el historial.
                </Typography>
              ) : null}
            </Box>
            <CapabilitySwitch
              compact={compact}
              label="Puede usarse como membresía"
              detail="Lo habilita como tipo de membresía para nuevas asignaciones."
              checked={Boolean(form.membershipEligible)}
              onChange={(value) => setForm((current) => ({
                ...current,
                membershipEligible: value,
                membershipPeriodValue: current.membershipPeriodValue || "1",
                membershipPeriodUnit: current.membershipPeriodUnit || "months",
                membershipReferencePrice: value && (current.membershipReferencePrice === "" || current.membershipReferencePrice === null || current.membershipReferencePrice === undefined)
                  ? String(current.price ?? "")
                  : current.membershipReferencePrice,
              }))}
            />
            {form.membershipEligible ? (
              <>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      type="number"
                      label="Precio por período"
                      value={form.membershipReferencePrice}
                      onChange={(event) => change("membershipReferencePrice", event.target.value)}
                      inputProps={{ min: 0, step: "any" }}
                      helperText="Se sugiere al asignar o renovar."
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      type="number"
                      label="Cada"
                      value={form.membershipPeriodValue}
                      onChange={(event) => change("membershipPeriodValue", event.target.value)}
                      inputProps={{ min: 1, max: 10000, step: 1 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      select
                      label="Periodicidad"
                      value={form.membershipPeriodUnit}
                      onChange={(event) => change("membershipPeriodUnit", event.target.value)}
                      fullWidth
                    >
                      <MenuItem value="days">Días</MenuItem>
                      <MenuItem value="weeks">Semanas</MenuItem>
                      <MenuItem value="months">Meses</MenuItem>
                      <MenuItem value="years">Años</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
                <CapabilitySwitch
                  compact={compact}
                  label="Controlar usos incluidos"
                  detail="Define un cupo independiente del stock físico del ítem."
                  checked={Boolean(form.membershipUsageEnabled)}
                  onChange={(value) => change("membershipUsageEnabled", value)}
                />
                {form.membershipUsageEnabled ? (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        type="number"
                        label="Cantidad incluida por período"
                        value={form.membershipIncludedUses}
                        onChange={(event) => change("membershipIncludedUses", event.target.value)}
                        inputProps={{ min: 1, max: 1000000, step: 1 }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Nombre de la unidad"
                        value={form.membershipUsageLabel}
                        onChange={(event) => change("membershipUsageLabel", event.target.value)}
                        inputProps={{ maxLength: 60 }}
                        placeholder="usos, sesiones, accesos…"
                        helperText="Terminología genérica para este plan."
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                ) : null}
              </>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      {canManageCosts && (form.purchasable || tracksInventory || form.expenseEnabled) ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                Costo interno
              </Typography>
              {!compact ? (
                <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                  Las compras confirmadas actualizan el último costo y el promedio. El costo de referencia sirve como valor inicial o estimado y no se muestra al público.
                </Typography>
              ) : null}
            </Box>
            {!hasUsefulCost && form.sellable ? (
              <Alert severity="warning">
                Cargá al menos un costo para que las ventas reflejen un margen real.
              </Alert>
            ) : null}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  label="Último costo"
                  value={form.lastCost}
                  InputProps={{ readOnly: true }}
                  inputProps={{ min: 0, step: "any" }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  label="Costo promedio"
                  value={form.averageCost}
                  InputProps={{ readOnly: true }}
                  inputProps={{ min: 0, step: "any" }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  label="Costo de referencia"
                  value={form.standardCost}
                  onChange={(event) => change("standardCost", event.target.value)}
                  inputProps={{ min: 0, step: "any" }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Costo que debe usar el sistema"
                  value={form.costingMethod}
                  onChange={(event) => change("costingMethod", event.target.value)}
                  fullWidth
                >
                  <MenuItem value="last_cost">Último costo</MenuItem>
                  <MenuItem value="average_cost">Costo promedio</MenuItem>
                  <MenuItem value="standard_cost">Costo de referencia</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      {canManageAccounting ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                Cuentas e impuestos
              </Typography>
              {!compact ? (
                <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                  Estas decisiones se copian en cada operación para conservar el historial aunque después cambie el ítem.
                </Typography>
              ) : null}
            </Box>

            {!accounts.length ? (
              <Alert severity="warning">
                No se pudieron cargar las cuentas. Actualizá la estructura del sistema antes de guardar.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {form.sellable ? (
                  <Grid item xs={12} md={6}>
                    <AccountSelect
                      label="Dónde registrar las ventas"
                      value={form.salesAccountCode}
                      onChange={(event) => change("salesAccountCode", event.target.value)}
                      accounts={accounts}
                      allowedTypes={["income"]}
                    />
                  </Grid>
                ) : null}
                {form.purchasable ? (
                  <Grid item xs={12} md={6}>
                    <AccountSelect
                      label="Dónde registrar las compras"
                      value={form.purchaseAccountCode}
                      onChange={(event) => change("purchaseAccountCode", event.target.value)}
                      accounts={accounts}
                      allowedTypes={["expense", "asset"]}
                    />
                  </Grid>
                ) : null}
                {tracksInventory ? (
                  <>
                    <Grid item xs={12} md={6}>
                      <AccountSelect
                        label="Valor del inventario"
                        value={form.inventoryAccountCode}
                        onChange={(event) => change("inventoryAccountCode", event.target.value)}
                        accounts={accounts}
                        allowedTypes={["asset"]}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <AccountSelect
                        label="Costo de lo vendido"
                        value={form.costAccountCode}
                        onChange={(event) => change("costAccountCode", event.target.value)}
                        accounts={accounts}
                        allowedTypes={["expense"]}
                      />
                    </Grid>
                  </>
                ) : null}
                {form.expenseEnabled || form.internalConsumptionEnabled ? (
                  <Grid item xs={12} md={6}>
                    <AccountSelect
                      label="Dónde registrar el gasto"
                      value={form.expenseAccountCode}
                      onChange={(event) => change("expenseAccountCode", event.target.value)}
                      accounts={accounts}
                      allowedTypes={["expense"]}
                    />
                  </Grid>
                ) : null}
                {(form.sellable || form.purchasable || form.expenseEnabled) ? (
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Impuesto aplicable"
                      value={form.taxProfileCode || ""}
                      onChange={(event) => change("taxProfileCode", event.target.value)}
                      fullWidth
                    >
                      <MenuItem value="">Sin impuesto configurado</MenuItem>
                      {activeTaxProfiles.map((profile) => (
                        <MenuItem key={profile.code} value={profile.code}>
                          {profile.name}
                          {Number(profile.rate || 0)
                            ? profile.calculationMode === "fixed"
                              ? ` · importe fijo ${Number(profile.rate)}`
                              : ` · ${Number(profile.rate)}%`
                            : ""}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                ) : null}
              </Grid>
            )}

            {form.sellable ? (
              <Alert severity={selectedTax ? "success" : "warning"}>
                {selectedTax
                  ? `El ítem está preparado para calcular impuestos con ${selectedTax.name || "la configuración elegida"}. Las operaciones conservarán ese cálculo.`
                  : "El ítem todavía no está preparado para facturar: las ventas se registrarán sin impuesto."}
                {" "}La emisión de un comprobante fiscal requiere además un proveedor de facturación conectado al proyecto.
              </Alert>
            ) : null}
          </Stack>
        </Paper>
      ) : compact ? null : (
        <Alert severity="info">
          Los costos, las cuentas y los impuestos aparecen únicamente para personas autorizadas.
        </Alert>
      )}
    </Stack>
  );
}
